// @story HIR-HR2
// AssessmentApprovals — what is waiting for the school's approver, across
// every role. Each row opens its stage's own panel on the job's Assessment
// Pipeline tab, where the paper is read and approved or sent back (Shohini,
// 29 Oct 2026: design and approve from the job's pipeline; this page stays
// only as the list).
//
// Shohini's decisions, 4 Oct: a school should not have to supply its own
// question papers; nothing the agent — or a person — writes reaches a
// candidate until a person with the approving permission (for now the school
// administrator, recruitment:admin — Shohini, 5 Oct) has approved it,
// question by question. HR asks for a paper; HR cannot approve it.
//
// To design one: pick the role (type to find it — "PGT Maths") and ONE stage,
// then "Draft with AI" or "Write it myself"; both continue in that stage's panel.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipboardCheck, Loader2, CheckCircle2, AlertTriangle, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { Button, Card, Select } from '../../ds';
import RolePicker from '../../components/RolePicker';
import { Pill, STATUS_WORDS, KIND_WORDS } from './ProposalReview';

/* The stage's panel, on the job's Assessment Pipeline tab. */
export const stagePanelLink = (jobId, stageId, { write = false } = {}) => {
  const q = new URLSearchParams({ tab: 'Assessment Pipeline', stage: stageId });
  if (write) q.set('write', '1');
  return `/jobs/${jobId}?${q.toString()}`;
};

// The stages that take a paper: a written test, or the AI interview's questions.
const takesPaper = (s) => !['human_interview', 'questionnaire'].includes(s.type)
  && (s.type === 'ai_audio_interview' || !['pre_screening', 'final_interview', 'ai_interview', 'communication', 'psychoanalytical', 'coding', 'gen_ai_readiness', 'situational_judgement'].includes(s.type));

/* ── Design one stage's assessment ───────────────────────────────────────── */
function DesignOneStage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [pipeline, setPipeline] = useState([]);
  const [stageId, setStageId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/jobs', { params: { limit: 100 } })
      .then((r) => setJobs(r.data?.data?.jobs || r.data?.data || r.data?.jobs || []))
      .catch(() => setJobs([]));
  }, []);
  useEffect(() => {
    setStageId(''); setPipeline([]);
    if (!jobId) return;
    api.get(`/jobs/${jobId}/pipeline`).then((r) => setPipeline(r.data?.data?.pipeline || [])).catch(() => setPipeline([]));
  }, [jobId]);

  const stages = pipeline.filter(takesPaper);
  const draft = async () => {
    setBusy(true);
    try {
      await api.post('/assessment-proposals/design', { jobId, stageId });
      toast.success('The Assessment Designer has drafted it. It is ready to review in the stage.');
      navigate(stagePanelLink(jobId, stageId));
    } catch (e) {
      toast.error(e.response?.data?.message || 'The Assessment Designer could not draft it just now.');
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <h2 style={{ fontSize: 'var(--fs-display-sm)', fontWeight: 600, margin: 0 }}>Design one assessment</h2>
      <p style={{ margin: '6px 0 16px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)', maxWidth: 640 }}>
        Choose a role and one of its stages. The Assessment Designer can draft it — using the role and the school's earlier
        approved papers for similar roles — or you can write it yourself. Either way the school's approver approves it before any candidate sees it.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RolePicker label="Role" roles={jobs.map((j) => ({ id: j._id, title: j.title }))} value={jobId} onChange={setJobId} />
        <Select label="Stage" value={stageId} onChange={setStageId} disabled={!stages.length}
          placeholder={jobId && !pipeline.length ? 'This role has no stages yet' : 'Choose a stage…'}
          options={stages.map((s) => ({ value: s.stageId, label: `${s.label}${s.type === 'ai_audio_interview' ? ' (AI interview questions)' : ' (written test)'}` }))} />
      </div>
      {jobId && !pipeline.length && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', marginTop: 8 }}>Set this role's stages on its Assessment Pipeline tab first.</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        <Button onClick={draft} loading={busy} disabled={!jobId || !stageId}>{busy ? 'Drafting — this takes a minute or two…' : 'Draft with AI'}</Button>
        <Button variant="secondary" disabled={!jobId || !stageId || busy} onClick={() => navigate(stagePanelLink(jobId, stageId, { write: true }))}>Write it myself</Button>
      </div>
    </Card>
  );
}

export default function AssessmentApprovals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [canApprove, setCanApprove] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/assessment-proposals', { params: filter ? { status: filter } : {} })
      .then((r) => { setRows(r.data?.data || []); setCanApprove(!!r.data?.canApprove); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => rows.length, [rows]);
  // What the last "Load an approved paper" did, kept on the page until closed —
  // a toast alone was easy to miss (Shohini, 6 Oct 2026).
  const [loadResult, setLoadResult] = useState(null);

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-dps-ink flex items-center gap-2"><ClipboardCheck size={22} /> Assessment approvals</h1>
        <p className="text-sm text-dps-ink-2 mt-1">Question papers and AI interview questions waiting for the school's approver, across every role. Open one to read it and approve it or send it back in its stage. None reaches a candidate until it is approved.</p>
      </div>
      <DesignOneStage />
      {canApprove && (
        <div className="card-dps p-6 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="font-heading font-semibold text-dps-ink">Load an approved paper</div>
            <p className="text-sm text-dps-ink-2">A paper already approved outside the console (a JSON file from the Assessment Designer). It is put on the role's written-test and AI interview stages with the approval note in the file, and recorded under your name.</p>
          </div>
          <label className="btn-dps cursor-pointer">
            {loadingFile ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />}
            {loadingFile ? 'Loading…' : 'Choose file'}
            <input type="file" accept="application/json,.json" className="hidden" disabled={loadingFile} onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (!f) return;
              setLoadingFile(true);
              setLoadResult({ state: 'loading', fileName: f.name });
              try {
                const file = JSON.parse(await f.text());
                const r = await api.post('/assessment-proposals/load-approved', {
                  jobId: file.jobId, role: file.role, draftedBy: file.draftedBy, approvalNote: file.approvalNote,
                  writtenTest: file.writtenTest, interview: file.interview,
                });
                const parts = (r.data?.data || []).map((x) => `${x.questions} questions on "${x.stage}"`);
                toast.success(parts.join('; ') || 'Loaded.');
                setLoadResult({ state: 'done', fileName: f.name, role: file.role?.title || '', parts });
                setFilter('approved');
                load();
              } catch (err) {
                const message = err.response?.data?.message || (err instanceof SyntaxError ? 'That file is not a paper file.' : 'Could not load it.');
                toast.error(message);
                setLoadResult({ state: 'error', fileName: f.name, message });
              } finally { setLoadingFile(false); }
            }} />
          </label>
          {loadResult && (
            <div role="status" aria-live="polite"
              className={`w-full rounded-dps border px-4 py-3 text-sm flex items-start gap-2 ${loadResult.state === 'error' ? 'border-red-300 bg-red-50 text-red-800' : loadResult.state === 'done' ? 'border-green-300 bg-green-50 text-green-900' : 'border-dps-hairline bg-dps-bg text-dps-ink-2'}`}>
              {loadResult.state === 'loading' && <Loader2 size={16} className="animate-spin mt-0.5 shrink-0" />}
              {loadResult.state === 'done' && <CheckCircle2 size={16} className="mt-0.5 shrink-0" />}
              {loadResult.state === 'error' && <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
              <div className="flex-1">
                {loadResult.state === 'loading' && <>Loading <strong>{loadResult.fileName}</strong>…</>}
                {loadResult.state === 'done' && (
                  <>
                    <div className="font-semibold">Loaded and approved under your name{loadResult.role ? ` for ${loadResult.role}` : ''}.</div>
                    <div>{loadResult.parts.length ? loadResult.parts.join(' · ') : 'The paper was loaded.'}</div>
                    <div className="text-xs mt-1 opacity-80">From {loadResult.fileName}. It is listed below under Approved. Nothing has been sent to candidates.</div>
                  </>
                )}
                {loadResult.state === 'error' && <><div className="font-semibold">Not loaded.</div><div>{loadResult.message}</div><div className="text-xs mt-1 opacity-80">From {loadResult.fileName}. Nothing was changed.</div></>}
              </div>
              {loadResult.state !== 'loading' && (
                <button type="button" onClick={() => setLoadResult(null)} className="text-xs underline shrink-0">Close</button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="card-dps overflow-hidden">
        <div className="card-dps-header flex flex-wrap items-center gap-2">
          {['pending', 'approved', 'rejected', ''].map((s) => (
            <button key={s || 'all'} type="button" onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1 rounded-pill ${filter === s ? 'bg-dps-ink text-white' : 'text-dps-ink-2'}`}>
              {s ? STATUS_WORDS[s] : 'All saved'}
            </button>
          ))}
          <span className="ml-auto text-xs text-dps-ink-3">{counts} shown</span>
        </div>
        {loading ? <div className="p-6 text-dps-ink-3 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div> : (
          rows.length === 0 ? <p className="p-6 text-sm text-dps-ink-3">Nothing here.</p> : (
            <ul>
              {rows.map((r) => (
                <li key={r.proposalId} className="border-t border-dps-hairline">
                  <button type="button" onClick={() => navigate(stagePanelLink(r.jobId, r.stageId))} className="w-full text-left px-6 py-4 hover:bg-dps-bg flex flex-wrap items-center gap-3"
                    aria-label={`${r.role?.title || 'Role'}, ${r.stage}: open the stage`}>
                    <span className="font-semibold text-dps-ink">{r.role?.title}</span>
                    <span className="text-sm text-dps-ink-2">{KIND_WORDS[r.kind]} · {r.stage} · {r.questions} questions</span>
                    {r.needsTeacherCheck > 0 && <span className="text-xs text-dps-ink flex items-center gap-1"><AlertTriangle size={12} /> {r.needsTeacherCheck} need a teacher's check</span>}
                    <span className="ml-auto flex items-center gap-2"><Pill status={r.status} /><ChevronRight size={16} aria-hidden="true" /></span>
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}
